import { useSnapshot } from 'valtio'
import cn from 'classnames'
import AsyncSelect from 'react-select/async'
import { type ReactElement } from 'react'
import { StandardInput } from '../ui/forms/StandardInput'
import { proxyGlobalAuth } from '../../proxies/proxyGlobalAuth'
import { countryCodes, usStates } from '../../utils/addressUtils'
import { toTitleCase } from '../../utils/formattingUtils'
import { buildOption, throttledLoadOptions } from '../../../routes/app/virtual-terminal/index/Payment/PaymentDetails/CustomerSection/utils'
import { customerAddProxy } from './customerAddProxy'

import { FormFieldNamesEnum } from './formFieldNamesEnum'
import { handleBlur, handleChange, handleSelectParentCustomer } from './utils'

export function CustomerSectionSlideContents (): ReactElement {
  const {
    parentCustomer,
    showStateError,
    showCityError,
    showCompanyNameError,
    showEmailError,
    showFirstNameError,
    showLastNameError,
    showStreetAddressOneError,
    showPostalCodeError,
    showCountryCodeError,
    formErrors,
    formValues
  } = useSnapshot(customerAddProxy, { sync: true })
  const { siteTheme: { horizontalLine } } = useSnapshot(proxyGlobalAuth)

  return (
    <>
      <div className="flex">
        <StandardInput
          label="Company Name"
          value={formValues.companyName || ''}
          onChange={handleChange(FormFieldNamesEnum.CompanyName)}
          onBlur={handleBlur(FormFieldNamesEnum.CompanyName)}
          showError={showCompanyNameError}
          errorMessage={formErrors.companyName || ''}
        />
      </div>

      <div className="flex">
        <StandardInput
          label="First Name"
          value={formValues.firstName || ''}
          onChange={handleChange(FormFieldNamesEnum.FirstName)}
          onBlur={handleBlur(FormFieldNamesEnum.FirstName)}
          showError={showFirstNameError}
          errorMessage={formErrors.firstName || ''}
        />

        <StandardInput
          label="Last Name"
          value={formValues.lastName || ''}
          onChange={handleChange(FormFieldNamesEnum.LastName)}
          onBlur={handleBlur(FormFieldNamesEnum.LastName)}
          showError={showLastNameError}
          errorMessage={formErrors.lastName || ''}
        />
      </div>

      <div className="flex">
        <StandardInput
          label="Email"
          value={formValues.email || ''}
          onChange={handleChange(FormFieldNamesEnum.Email)}
          onBlur={handleBlur(FormFieldNamesEnum.Email)}
          showError={showEmailError}
          errorMessage={formErrors.email || ''}
        />
      </div>

      <hr className={cn(horizontalLine.secondaryClasses, 'mt-2 mb-3 mx-2 border border-gray-400')} />

      <div className="flex">
        <StandardInput
          label="Shipping Address"
          placeholder="Address Line 1"
          value={formValues.streetAddressOne || ''}
          onChange={handleChange(FormFieldNamesEnum.StreetAddressOne)}
          onBlur={handleBlur(FormFieldNamesEnum.StreetAddressOne)}
          showError={showStreetAddressOneError}
          errorMessage={formErrors.streetAddressOne || ''}
        />
      </div>

      <div className="flex">
        <StandardInput
          value={formValues.streetAddressTwo || ''}
          placeholder="Address Line 2"
          onChange={handleChange(FormFieldNamesEnum.StreetAddressTwo)}
          onBlur={handleBlur(FormFieldNamesEnum.StreetAddressTwo)}
          errorMessage={formErrors.streetAddressTwo || ''}
        />
      </div>

      <div className="flex">
        <StandardInput
          label="City"
          value={formValues.city || ''}
          onChange={handleChange(FormFieldNamesEnum.City)}
          onBlur={handleBlur(FormFieldNamesEnum.City)}
          showError={showCityError}
          errorMessage={formErrors.city || ''}
        />
      </div>

      <div className="flex">

        <div className="flex flex-col w-full mx-2">
          <label>State</label>
          <div className="relative">
            <select
              className=" pt-2 pb-1.5 pl-3 text-left w-full  border border-wpy-gray rounded-md"
              onChange={handleChange(FormFieldNamesEnum.State)}
              onBlur={handleBlur(FormFieldNamesEnum.State)}
              value={formValues.state || ''}
              required
            >
              <option value="" disabled>Choose State</option>
              {
                  usStates.map(s => (
                    <option key={s.name} value={s.abbreviation}>{toTitleCase(s.name)}</option>)
                  )
              }
            </select>
            {showStateError && (
              <div className="h-5 absolute left-0.5 top-1 text-lg text-red">❗</div>
            )}
          </div>
          <div className="h-6 text-sm text-red-500">{formErrors.state}</div>
        </div>

        <StandardInput
          label="Zip / Postal Code"
          value={formValues.postalCode || ''}
          onChange={handleChange(FormFieldNamesEnum.PostalCode)}
          onBlur={handleBlur(FormFieldNamesEnum.PostalCode)}
          showError={showPostalCodeError}
          errorMessage={formErrors.postalCode ?? ''}
        />

      </div>

      <div className="flex">

        <div className="flex flex-col w-full mx-2">
          <label>Country</label>
          <div className="relative">
            <select
              className="pt-2 pb-1.5 pl-3 text-left w-full border border-wpy-gray rounded-md"
              onChange={handleChange(FormFieldNamesEnum.CountryCode)}
              onBlur={handleBlur(FormFieldNamesEnum.CountryCode)}
              value={formValues.countryCode ?? ''}
              required
            >
              <option value="" disabled>Choose Country</option>
              {
                countryCodes.map(s => (
                  <option key={s.name} value={s.abbreviation}>{toTitleCase(s.name)}</option>)
                )
            }
            </select>
            {showCountryCodeError && (
              <div className="h-5 absolute left-2 top-1 text-lg text-red">❗</div>
            )}
          </div>
          <div className="h-6 text-sm text-red-500">{formErrors.countryCode}</div>
        </div>
      </div>

      <hr className={cn(horizontalLine.secondaryClasses, 'mt-2 mb-3 mx-2 border border-gray-400')} />

      <div className="flex flex-col w-full mx-2 mb-4">
        <label>Parent Customer (optional)</label>
        <div className="flex flex-row">

          <AsyncSelect
            key={parentCustomer?.id}
            isClearable
            isSearchable
            className="flex-grow mr-4 border border-wpy-gray rounded-md"
            defaultOptions
            loadOptions={throttledLoadOptions}
            onChange={handleSelectParentCustomer}
            placeholder="Select Parent Customer"
            styles={{
              control: (provided) => ({
                ...provided,
                border: 0,
                height: '100%'
              })
            }}
            tabSelectsValue
            value={parentCustomer ? buildOption(parentCustomer) : null}
          />

        </div>
      </div>

    </>
  )
}
